import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Plus } from "lucide-react";
import SteckLogo from "./SteckLogo";
import {
  FRICTION_QUESTION,
  TENSION_QUESTION,
  INDUSTRIES,
  LEVERS,
  computeTwoDoorsResult,
  buildTwoDoorsPayload,
} from "../data/travelAssessment";
import { submitInquiry } from "../lib/api";

/**
 * Two Doors — the single, unified interactive tool that replaces the old
 * travel-needs survey AND the "Gauge your needs" tab/launcher. A full-screen,
 * premium night-sky overlay with a gold accent (scoped to this experience only;
 * the rest of the site stays McKinsey-blue).
 *
 * Flow (one state machine):
 *   door ─▶ family ─────────────────────────┐
 *        └▶ business (industry) ────────────┤
 *                                            ▼
 *                       friction ─▶ tension ─▶ result ─▶ (map | aircraft | close)
 *
 * The three preserved pieces of the old survey are integrated verbatim in
 * meaning: the friction question, the 1–10 tension ranking, and the
 * recommended-reading result (see computeTwoDoorsResult). Progress is persisted
 * in sessionStorage so the collapsed module can resume or re-show a result.
 *
 * Props:
 *   open        — whether the overlay is visible (from useTravelAssessment)
 *   onClose     — (reason) => void   reason ∈ button|escape|explore|navigate|secondary
 *   onComplete  — (payload) => void  fired once when the result is reached
 */

const STATE_KEY = "steck_2d_state";
const ENTRY_KEY = "steck_2d_entry";
const SCROLL_MAP_KEY = "steck_scroll_to_map";
const MAP_ROUTES = new Set(["/", "/insights"]);
const AIRCRAFT_ROUTE = "/aircraft/beechjet-400a";

const readState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) || "null");
  } catch (e) {
    return null;
  }
};
const writeState = (s) => {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(s));
  } catch (e) {
    /* ignore */
  }
};
const takeEntry = () => {
  try {
    const e = sessionStorage.getItem(ENTRY_KEY);
    sessionStorage.removeItem(ENTRY_KEY);
    return e;
  } catch (e) {
    return null;
  }
};

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

const TwoDoorsExperience = ({ open, onClose, onComplete }) => {
  const dialogRef = useRef(null);
  const advanceTimer = useRef(null);
  const completedRef = useRef(false);
  const wasOpen = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [shown, setShown] = useState(false); // entrance transition
  const [screen, setScreen] = useState("door"); // door|family|business|friction|tension|result
  const [path, setPath] = useState(null); // family|business
  const [industry, setIndustry] = useState(null); // industry id (business)
  const [bizIndex, setBizIndex] = useState(0); // displayed industry card
  const [bizLocked, setBizLocked] = useState(false); // chip chosen → rotation stops
  const [friction, setFriction] = useState(null);
  const [tension, setTension] = useState(null);
  const [pendingTension, setPendingTension] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [note, setNote] = useState(""); // free-text message on the result
  const [noteName, setNoteName] = useState("");
  const [noteEmail, setNoteEmail] = useState("");
  const [noteSent, setNoteSent] = useState(false);
  const [noteSending, setNoteSending] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [noteOpen, setNoteOpen] = useState(false); // note stays collapsed until opened

  const onCloseRef = useRef(onClose);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const close = useCallback((reason) => {
    if (onCloseRef.current) onCloseRef.current(reason);
  }, []);

  // ── Restore / seed state on the rising edge of `open` ────────────────────
  // Unlike the old modal, this PRESERVES progress: a visitor who closed midway
  // returns to their unfinished step; one who finished returns to the result.
  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return undefined;
    }
    if (wasOpen.current) return undefined; // already open; don't re-seed
    wasOpen.current = true;

    const entry = takeEntry();
    const saved = readState() || {};
    completedRef.current = false;

    let s = {
      screen: "door",
      path: null,
      industry: null,
      bizIndex: 0,
      bizLocked: false,
      friction: null,
      tension: null,
      completed: false,
    };

    if (entry === "family") {
      s = { ...saved, screen: "family", path: "family" };
    } else if (entry === "business") {
      s = { ...saved, screen: "business", path: "business" };
    } else if (entry === "result" && saved.completed) {
      s = { ...saved, screen: "result" };
    } else if (saved.completed) {
      s = { ...saved, screen: "result" };
    } else if (saved.screen && saved.screen !== "door") {
      s = { ...saved };
    }

    completedRef.current = !!s.completed;
    setScreen(s.screen || "door");
    setPath(s.path ?? null);
    setIndustry(s.industry ?? null);
    setBizIndex(s.bizIndex ?? 0);
    setBizLocked(!!s.bizLocked);
    setFriction(s.friction ?? null);
    setTension(s.tension ?? null);
    setPendingTension(s.tension ?? null);
    setCompleted(!!s.completed);
    setNote("");
    setNoteName("");
    setNoteEmail("");
    setNoteSent(false);
    setNoteSending(false);
    setNoteError("");
    setNoteOpen(false);

    setShown(false);
    const raf = requestAnimationFrame(() => setShown(true));
    return () => {
      cancelAnimationFrame(raf);
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, [open]);

  // Persist progress continuously while open so a close (or a navigation to the
  // map / aircraft page) leaves a resumable trail for the collapsed module.
  useEffect(() => {
    if (!open) return;
    writeState({ screen, path, industry, bizIndex, bizLocked, friction, tension, completed });
  }, [open, screen, path, industry, bizIndex, bizLocked, friction, tension, completed]);

  // Lock background scroll (overflow:hidden, not position:fixed — avoids the
  // Safari backdrop-filter clipping bug and preserves scroll position).
  useLayoutEffect(() => {
    if (!open) return undefined;
    const docEl = document.documentElement;
    const body = document.body;
    const prev = {
      docOverflow: docEl.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPadRight: body.style.paddingRight,
    };
    const scrollbarW = window.innerWidth - docEl.clientWidth;
    docEl.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      docEl.style.overflow = prev.docOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.paddingRight = prev.bodyPadRight;
    };
  }, [open]);

  // Focus management: move focus in, trap Tab, Escape closes, restore on close.
  useEffect(() => {
    if (!open) return undefined;
    const prevActive = document.activeElement;
    const focusTimer = setTimeout(() => {
      const el = dialogRef.current;
      if (!el) return;
      // Focus a specific control only when a screen opts in via [data-autofocus];
      // otherwise focus the dialog container itself, so no door card is
      // pre-focused (which would pre-lift it). Tab still reaches every control.
      const target = el.querySelector("[data-autofocus]") || el;
      try {
        target.focus();
      } catch (e) {
        /* ignore */
      }
    }, 60);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close("escape");
        return;
      }
      if (e.key === "Tab") {
        const el = dialogRef.current;
        if (!el) return;
        const nodes = el.querySelectorAll(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey, true);
      if (prevActive && prevActive.focus) {
        try {
          prevActive.focus();
        } catch (e) {
          /* ignore */
        }
      }
    };
  }, [open, close, screen]);

  // Functional industry rotation — runs on desktop AND mobile, and is NOT gated
  // by reduced-motion (it's content, not decoration). Stops once a chip is locked.
  useEffect(() => {
    if (!open || screen !== "business" || bizLocked) return undefined;
    const id = setInterval(() => {
      setBizIndex((i) => (i + 1) % INDUSTRIES.length);
    }, 10000);
    return () => clearInterval(id);
  }, [open, screen, bizLocked]);

  // ── Result computation (fires onComplete exactly once) ───────────────────
  const goToResult = useCallback(
    (fricValue, tensValue) => {
      setScreen("result");
      setCompleted(true);
      if (!completedRef.current) {
        completedRef.current = true;
        const result = computeTwoDoorsResult({
          path,
          industry,
          primaryFriction: fricValue,
          impactScore: tensValue,
        });
        const payload = buildTwoDoorsPayload({
          path,
          industry,
          primaryFriction: fricValue,
          impactScore: tensValue,
          resultProfile: result.resultProfile,
        });
        if (onCompleteRef.current) onCompleteRef.current(payload);
      }
    },
    [path, industry]
  );

  // ── Screen handlers ──────────────────────────────────────────────────────
  const chooseDoor = (p) => {
    setPath(p);
    if (p === "business") {
      setBizLocked(false);
      setBizIndex(0);
      setScreen("business");
    } else {
      setScreen("family");
    }
  };

  const selectChip = (i) => {
    setBizIndex(i);
    setBizLocked(true);
  };

  const toFriction = () => {
    if (path === "business") setIndustry(INDUSTRIES[bizIndex].id);
    setScreen("friction");
  };

  const chooseFriction = (value) => {
    setFriction(value);
    const advance = () => setScreen("tension");
    if (prefersReduced()) advance();
    else {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(advance, 300);
    }
  };

  const confirmTension = () => {
    if (pendingTension == null) return;
    setTension(pendingTension);
    goToResult(friction, pendingTension);
  };

  const goBack = () => {
    if (screen === "result") {
      completedRef.current = false;
      setCompleted(false);
      setScreen("tension");
    } else if (screen === "tension") {
      setScreen("friction");
    } else if (screen === "friction") {
      setScreen(path === "business" ? "business" : "family");
    } else {
      setScreen("door");
    }
  };

  // "See how ownership works" → the Aircraft page (never a Management section).
  const goToAircraft = () => {
    close("navigate");
    if (location.pathname !== AIRCRAFT_ROUTE) navigate(AIRCRAFT_ROUTE);
  };

  // "Estimate your travel time" / "See your reach on the map" → the interactive
  // map. If the map is already mounted (Home) the overlay clears and Home glides
  // to it (onContinue on reason "explore"). From any other route, navigate to
  // Home and let it scroll once the map has mounted (SCROLL_MAP_KEY flag).
  const goToMap = () => {
    if (MAP_ROUTES.has(location.pathname)) {
      close("explore");
    } else {
      try {
        sessionStorage.setItem(SCROLL_MAP_KEY, "1");
      } catch (e) {
        /* ignore */
      }
      close("navigate");
      navigate("/");
    }
  };

  // Note to Steck — routed through the shared /api/inquiries endpoint, which
  // emails INQUIRY_RECIPIENT (Brian today) and logs it in the admin inbox. Same
  // pipeline as every other contact form, so the recipient stays centralized.
  // The message is a note about the visitor's travel needs — never a booking.
  const sendNote = async () => {
    const msg = note.trim();
    if (!msg || !noteName.trim() || !isEmail(noteEmail) || noteSending) return;
    setNoteSending(true);
    setNoteError("");

    const fricOpt = FRICTION_QUESTION.options.find((o) => o.value === friction);
    const ind = INDUSTRIES.find((i) => i.id === industry);
    const context = [
      `Path: ${path === "business" ? "Business" : "Family"}`,
      ind ? `Industry: ${ind.chip}` : null,
      fricOpt ? `Greatest friction: ${fricOpt.label}` : null,
      tension != null ? `Weight: ${tension}/10` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await submitInquiry({
        name: noteName.trim(),
        email: noteEmail.trim(),
        company: "",
        message: `${msg}\n\n— Sent from the Two Doors travel assessment —\n${context}`,
        report_slug: "two-doors-assessment",
        report_title: "Two Doors travel assessment",
      });
      setNoteSent(true);
      // Preserve the local completion payload behaviour as well.
      if (onCompleteRef.current) {
        const r = computeTwoDoorsResult({ path, industry, primaryFriction: friction, impactScore: tension });
        onCompleteRef.current(
          buildTwoDoorsPayload({
            path,
            industry,
            primaryFriction: friction,
            impactScore: tension,
            resultProfile: r.resultProfile,
          }),
          msg
        );
      }
    } catch (e) {
      setNoteError("Something went wrong. Please try again, or email contact@steckaviationllc.com.");
    } finally {
      setNoteSending(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const activeIndustry = INDUSTRIES[bizIndex];
  const result =
    screen === "result"
      ? computeTwoDoorsResult({ path, industry, primaryFriction: friction, impactScore: tension })
      : null;
  const showBack = screen !== "door";

  return createPortal(
    <div className={`td-root${shown ? " is-shown" : ""}`} data-testid="two-doors">
      {/* ── Night-sky ground ─────────────────────────────────────────────── */}
      <div className="td-ground" aria-hidden>
        <svg className="td-rings" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice" aria-hidden>
          {[140, 250, 360, 470].map((r) => (
            <circle key={r} cx="500" cy="500" r={r} />
          ))}
        </svg>
        <div className="td-horizon" />
      </div>
      {/* Hairline frame + gold corner ticks */}
      <div className="td-frame" aria-hidden>
        <span className="td-tick td-tick--tl" />
        <span className="td-tick td-tick--tr" />
        <span className="td-tick td-tick--bl" />
        <span className="td-tick td-tick--br" />
      </div>

      {/* ── Fixed chrome ─────────────────────────────────────────────────── */}
      <div className="td-chrome-tl">
        <div className="td-logo">
          <SteckLogo size={48} variant="white" />
        </div>
        {showBack && (
          <button type="button" className="td-back" onClick={goBack} data-testid="two-doors-back">
            <ArrowLeft size={15} strokeWidth={2} aria-hidden /> Back
          </button>
        )}
      </div>
      <button
        type="button"
        className="td-close"
        onClick={() => close("button")}
        aria-label="Close"
        data-testid="two-doors-close"
      >
        <X size={19} strokeWidth={2} aria-hidden />
      </button>

      {/* ── Stage ────────────────────────────────────────────────────────── */}
      <div
        ref={dialogRef}
        className="td-stage"
        role="dialog"
        aria-modal="true"
        aria-label="Explore private aviation with Steck Aviation"
        tabIndex={-1}
      >
        {/* DOOR ------------------------------------------------------------ */}
        {screen === "door" && (
          <div className="td-screen td-door-screen" key="door">
            <h1 className="td-h1">
              Same aircraft.
              <br />
              <i>Two kinds of freedom.</i>
            </h1>
            <p className="td-sub">Everyone has a reason. Choose yours.</p>

            <div className="td-doors">
              <button
                type="button"
                className="td-door td-door--1"
                onClick={() => chooseDoor("family")}
                data-testid="two-doors-family"
              >
                <span className="td-door__num">I.</span>
                <span className="td-door__pill">
                  OPEN <ArrowRight size={13} strokeWidth={2.4} aria-hidden />
                </span>
                <span className="td-door__title">For the Family</span>
                <span className="td-door__tag">Get where you actually want to be.</span>
              </button>

              <button
                type="button"
                className="td-door td-door--2"
                onClick={() => chooseDoor("business")}
                data-testid="two-doors-business"
              >
                <span className="td-door__num">II.</span>
                <span className="td-door__pill">
                  OPEN <ArrowRight size={13} strokeWidth={2.4} aria-hidden />
                </span>
                <span className="td-door__title">For Business</span>
                <span className="td-door__tag">Get your time back.</span>
              </button>
            </div>
          </div>
        )}

        {/* FAMILY ---------------------------------------------------------- */}
        {screen === "family" && (
          <div className="td-screen td-family-screen" key="family">
            <p className="td-eyebrow">For the Family</p>
            <h2 className="td-h2">
              The same aircraft. A completely different reason to love it.
            </h2>
            <p className="td-body">
              No TSA. No layovers. No vacation days spent in line. Take the family
              exactly where you want to go—the lake, the beach, the game—and bring
              them home the same weekend. More time there. Less everything else.
            </p>
            <div className="td-cta-row">
              <button
                type="button"
                className="td-btn td-btn--primary"
                onClick={toFriction}
                data-testid="two-doors-family-primary"
                data-autofocus=""
              >
                See where Steck Aviation can take you <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* BUSINESS -------------------------------------------------------- */}
        {screen === "business" && (
          <div className="td-screen td-biz-screen" key="business">
            <p className="td-eyebrow td-eyebrow--center">For Business, what&apos;s your world?</p>
            <div className="td-chips" role="group" aria-label="Choose your industry">
              {INDUSTRIES.map((ind, i) => (
                <button
                  key={ind.id}
                  type="button"
                  className={`td-chip${i === bizIndex ? " is-active" : ""}`}
                  onClick={() => selectChip(i)}
                  aria-pressed={i === bizIndex}
                  data-testid={`two-doors-chip-${ind.id}`}
                >
                  {ind.chip}
                </button>
              ))}
            </div>

            <div className="td-indcard" key={activeIndustry.id} data-testid="two-doors-industry-card">
              <h2 className="td-indcard__headline">{activeIndustry.headline}</h2>
              <p className="td-indcard__body">{activeIndustry.body}</p>
            </div>

            <div className="td-cta-row td-cta-row--biz">
              <button
                type="button"
                className="td-btn td-btn--primary"
                onClick={toFriction}
                data-testid="two-doors-biz-primary"
              >
                Estimate your travel time <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </button>
              <button
                type="button"
                className="td-btn td-btn--ghost"
                onClick={goToAircraft}
                data-testid="two-doors-biz-ownership"
              >
                See how ownership works <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* FRICTION -------------------------------------------------------- */}
        {screen === "friction" && (
          <div className="td-screen td-q-screen" key="friction">
            <h2 className="td-q-prompt">{FRICTION_QUESTION.prompt}</h2>
            <div className="td-options" role="group" aria-label={FRICTION_QUESTION.prompt}>
              {FRICTION_QUESTION.options.map((opt, i) => {
                const selected = friction === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`td-option${selected ? " is-selected" : ""}`}
                    onClick={() => chooseFriction(opt.value)}
                    data-autofocus={i === 0 ? "" : undefined}
                    data-testid={`two-doors-friction-${opt.value}`}
                  >
                    <span className="td-option__label">{opt.label}</span>
                    <span className="td-option__body">{opt.body}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* TENSION --------------------------------------------------------- */}
        {screen === "tension" && (
          <div className="td-screen td-q-screen" key="tension">
            <h2 className="td-q-prompt">{TENSION_QUESTION.prompt}</h2>
            <div
              className="td-scale"
              role="group"
              aria-label={`${TENSION_QUESTION.prompt} — ${TENSION_QUESTION.min} is ${TENSION_QUESTION.minLabel}, ${TENSION_QUESTION.max} is ${TENSION_QUESTION.maxLabel}`}
            >
              {Array.from({ length: TENSION_QUESTION.max - TENSION_QUESTION.min + 1 }, (_, i) => {
                const n = TENSION_QUESTION.min + i;
                const active = pendingTension === n;
                return (
                  <button
                    key={n}
                    type="button"
                    className={`td-dot${active ? " is-active" : ""}`}
                    onClick={() => setPendingTension(n)}
                    aria-pressed={active}
                    aria-label={
                      n === TENSION_QUESTION.min
                        ? `${n} — ${TENSION_QUESTION.minLabel}`
                        : n === TENSION_QUESTION.max
                          ? `${n} — ${TENSION_QUESTION.maxLabel}`
                          : String(n)
                    }
                    data-autofocus={i === 0 ? "" : undefined}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <div className="td-scale__labels">
              <span>{TENSION_QUESTION.minLabel}</span>
              <span>{TENSION_QUESTION.maxLabel}</span>
            </div>
            <div className="td-cta-row td-cta-row--center">
              <button
                type="button"
                className="td-btn td-btn--primary"
                onClick={confirmTension}
                disabled={pendingTension == null}
                data-testid="two-doors-tension-continue"
              >
                See your recommendation <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* RESULT ---------------------------------------------------------- */}
        {screen === "result" && result && (
          <div className="td-screen td-result-screen" key="result">
            <p className="td-eyebrow td-eyebrow--center">Your reach</p>
            <h2 className="td-result__headline">{result.headline}</h2>
            <p className="td-result__body">{result.body}</p>

            <div className="td-result-grid">
              {/* Left spacer balances the reading rail so the main column sits
                  dead-center on wide viewports. */}
              <div className="td-result-spacer" aria-hidden />

              {/* Main column — the recommendation + the two actions, centered. */}
              <div className="td-result-main">
                <ul className="td-levers" aria-label="Where ownership changes the math">
                  {LEVERS.map((lv) => (
                    <li key={lv.key} className={`td-lever${lv.key === result.emphasisKey ? " is-emphasis" : ""}`}>
                      <span className="td-lever__tick" aria-hidden />
                      {lv.line}
                    </li>
                  ))}
                </ul>

                <div className="td-cta-row td-cta-row--result">
                  <button
                    type="button"
                    className="td-btn td-btn--primary td-btn--lg"
                    onClick={goToMap}
                    data-autofocus=""
                    data-testid="two-doors-result-map"
                  >
                    See your reach on the map <ArrowRight size={17} strokeWidth={2} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="td-btn td-btn--ghost td-btn--lg"
                    onClick={goToAircraft}
                    data-testid="two-doors-result-ownership"
                  >
                    See how ownership works
                  </button>
                </div>
              </div>

              {/* Aside — recommended reading, kept off to the side so it doesn't
                  interrupt the recommendation. */}
              {result.recommendedArticle && (
                <aside className="td-result-aside">
                  <Link
                    to={`/reports/${result.recommendedArticle.slug}`}
                    className="td-related"
                    data-testid="two-doors-article"
                  >
                    <span className="td-related__eyebrow">Recommended reading</span>
                    <span className="td-related__title">
                      {result.recommendedArticle.title}
                      <ArrowRight size={14} strokeWidth={2} aria-hidden />
                    </span>
                  </Link>
                </aside>
              )}
            </div>

            {/* A note to Steck — its own centered block at the very end, kept
                collapsed so it doesn't pull focus from continuing. Framed around
                tailoring services for an owner, never booking a flight. */}
            <div className="td-note">
              {noteSent ? (
                <p className="td-note__done" role="status">
                  Thank you. We&apos;ll take it into account.
                </p>
              ) : noteOpen ? (
                <>
                  <label className="td-note__label" htmlFor="td-note-input">
                    Anything you&apos;d like Steck to know about your trip or specific needs?
                  </label>
                  <textarea
                    id="td-note-input"
                    className="td-note__input"
                    rows={3}
                    placeholder="A few words help us tailor things."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="two-doors-note-input"
                  />
                  <div className="td-note__row">
                    <input
                      type="text"
                      className="td-note__field"
                      placeholder="Your name"
                      aria-label="Your name"
                      value={noteName}
                      onChange={(e) => setNoteName(e.target.value)}
                      data-testid="two-doors-note-name"
                    />
                    <input
                      type="email"
                      className="td-note__field"
                      placeholder="Your email"
                      aria-label="Your email"
                      value={noteEmail}
                      onChange={(e) => setNoteEmail(e.target.value)}
                      data-testid="two-doors-note-email"
                    />
                  </div>
                  {noteError && (
                    <p className="td-note__error" role="alert">{noteError}</p>
                  )}
                  <button
                    type="button"
                    className="td-btn td-btn--ghost td-note__send"
                    onClick={sendNote}
                    disabled={noteSending || !note.trim() || !noteName.trim() || !isEmail(noteEmail)}
                    data-testid="two-doors-note-send"
                  >
                    {noteSending ? "Sending…" : "Share with Steck"}
                    {!noteSending && <ArrowRight size={15} strokeWidth={2} aria-hidden />}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="td-note__toggle"
                  onClick={() => setNoteOpen(true)}
                  data-testid="two-doors-note-toggle"
                >
                  <Plus size={14} strokeWidth={2.2} aria-hidden /> Add a note about your trip or needs
                </button>
              )}
            </div>

            <button
              type="button"
              className="td-secondary"
              onClick={() => close("secondary")}
              data-testid="two-doors-result-continue"
            >
              Continue browsing
            </button>

            {/* Compliance line — moved to the very bottom, deliberately quiet. */}
            {result.positioning && (
              <p className="td-positioning td-positioning--foot">{result.positioning}</p>
            )}
          </div>
        )}
      </div>

      <style>{styles}</style>
    </div>,
    document.body
  );
};

const styles = `
  .td-root {
    --td-gold: #c8a46a; --td-gold-soft: #e2c894;
    --td-text: #eef3f8; --td-muted: #93a1b1; --td-ground: #080b11;
    position: fixed; inset: 0; z-index: 95;
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--td-text);
  }

  /* Ground ---------------------------------------------------------------- */
  .td-ground {
    position: absolute; inset: 0; overflow: hidden;
    background:
      radial-gradient(120% 90% at 50% 30%, rgba(200,164,106,0.10) 0%, transparent 42%),
      radial-gradient(140% 120% at 50% 8%, #0d1420 0%, #0a0f18 40%, var(--td-ground) 78%),
      var(--td-ground);
    opacity: 0; transition: opacity 700ms ease;
  }
  .td-root.is-shown .td-ground { opacity: 1; }
  .td-ground::after { /* vignette */
    content: ""; position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(120% 100% at 50% 45%, transparent 52%, rgba(2,4,8,0.72) 100%);
  }
  .td-rings {
    position: absolute; left: 50%; top: 46%; transform: translate(-50%,-50%);
    width: min(150vh, 150vw); height: min(150vh, 150vw); opacity: 0.5;
  }
  .td-rings circle {
    fill: none; stroke: rgba(200,164,106,0.13); stroke-width: 1;
  }
  .td-horizon {
    position: absolute; left: 0; right: 0; top: 55%; height: 1px;
    background: linear-gradient(to right, transparent, rgba(200,164,106,0.34) 20%, rgba(200,164,106,0.34) 80%, transparent);
    opacity: 0.7;
  }

  /* Hairline frame + corner ticks ----------------------------------------- */
  .td-frame {
    position: absolute; inset: 16px; pointer-events: none;
    border: 1px solid rgba(200,164,106,0.16); border-radius: 2px;
    opacity: 0; transition: opacity 900ms ease 120ms;
  }
  .td-root.is-shown .td-frame { opacity: 1; }
  .td-tick { position: absolute; width: 12px; height: 12px; }
  .td-tick::before, .td-tick::after { content: ""; position: absolute; background: var(--td-gold); opacity: 0.8; }
  .td-tick::before { width: 12px; height: 1px; } .td-tick::after { width: 1px; height: 12px; }
  .td-tick--tl { top: -1px; left: -1px; } .td-tick--tl::before { top: 0; left: 0; } .td-tick--tl::after { top: 0; left: 0; }
  .td-tick--tr { top: -1px; right: -1px; } .td-tick--tr::before { top: 0; right: 0; } .td-tick--tr::after { top: 0; right: 0; }
  .td-tick--bl { bottom: -1px; left: -1px; } .td-tick--bl::before { bottom: 0; left: 0; } .td-tick--bl::after { bottom: 0; left: 0; }
  .td-tick--br { bottom: -1px; right: -1px; } .td-tick--br::before { bottom: 0; right: 0; } .td-tick--br::after { bottom: 0; right: 0; }

  /* Chrome ---------------------------------------------------------------- */
  .td-chrome-tl {
    position: absolute; top: clamp(20px,3vh,34px); left: clamp(20px,3vw,40px); z-index: 6;
    display: flex; flex-direction: column; gap: 16px; align-items: flex-start;
  }
  .td-logo { opacity: 0.96; }
  .td-back {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 15px; border-radius: 999px; cursor: pointer;
    background: rgba(10,15,22,0.6); border: 1px solid rgba(200,164,106,0.22);
    -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    color: var(--td-muted); font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase;
    transition: color 200ms ease, border-color 200ms ease, background 200ms ease;
  }
  .td-back:hover { color: var(--td-text); border-color: rgba(200,164,106,0.5); }
  .td-back:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 3px; }
  .td-close {
    position: absolute; top: clamp(20px,3vh,34px); right: clamp(20px,3vw,40px); z-index: 6;
    width: 42px; height: 42px; border-radius: 10px; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(12,17,24,0.66); border: 1px solid rgba(255,255,255,0.12);
    -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
    color: rgba(238,243,248,0.72);
    transition: color 200ms ease, border-color 200ms ease, background 200ms ease;
  }
  .td-close:hover { color: #fff; border-color: rgba(200,164,106,0.5); background: rgba(20,26,34,0.8); }
  .td-close:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 3px; }

  /* Stage ----------------------------------------------------------------- */
  .td-stage {
    position: absolute; inset: 0; z-index: 4; outline: none;
    display: flex; align-items: center; justify-content: center;
    padding: clamp(96px,14vh,150px) clamp(22px,5vw,48px) clamp(48px,8vh,90px);
    overflow-y: auto;
  }
  .td-screen {
    width: 100%; max-width: 1040px; margin: auto; text-align: center;
    animation: td-rise 700ms cubic-bezier(.2,.7,.2,1) both;
  }
  @keyframes td-rise { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }

  .td-eyebrow {
    font-family: 'IBM Plex Mono', monospace; font-size: 12px; font-weight: 500;
    letter-spacing: 0.24em; text-transform: uppercase; color: var(--td-gold);
    margin: 0 0 18px;
  }
  .td-eyebrow--center { text-align: center; }

  /* Door screen ----------------------------------------------------------- */
  .td-h1 {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(40px, 6.4vw, 92px); line-height: 1.02; letter-spacing: -0.02em;
    color: #fff; margin: 0 0 22px;
  }
  .td-h1 i { font-style: italic; color: var(--td-gold-soft); }
  .td-sub { color: var(--td-muted); font-size: clamp(15px,1.4vw,19px); margin: 0 0 clamp(30px,5vh,54px); }
  .td-doors {
    display: grid; grid-template-columns: 1fr 1fr; gap: clamp(16px,2vw,28px);
    max-width: 900px; margin: 0 auto clamp(24px,4vh,40px);
  }
  .td-door {
    position: relative; text-align: left; cursor: pointer;
    min-height: 260px; padding: clamp(22px,2.4vw,32px);
    display: flex; flex-direction: column;
    border-radius: 16px; border: 1px solid rgba(200,164,106,0.42);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 22%),
      linear-gradient(180deg, rgba(16,22,32,0.72) 0%, rgba(9,13,20,0.86) 100%);
    box-shadow: 0 40px 90px -40px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.06);
    transition: transform 480ms cubic-bezier(.2,.7,.2,1), box-shadow 480ms ease, border-color 480ms ease;
  }
  /* Calm at rest (no perpetual bounce). The hover is the moment: a smooth,
     weighty lift with a soft gold bloom — nothing pre-selected on open. */
  .td-door:hover {
    transform: translateY(-8px) scale(1.012);
    border-color: rgba(200,164,106,0.75);
    box-shadow: 0 60px 120px -42px rgba(0,0,0,0.92), 0 0 44px -12px rgba(200,164,106,0.28), 0 0 0 1px rgba(200,164,106,0.22), inset 0 1px 0 rgba(255,255,255,0.09);
  }
  .td-door:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 4px; }
  .td-door__num {
    font-family: 'Fraunces', serif; font-size: 16px; color: rgba(200,164,106,0.6); letter-spacing: 0.06em;
  }
  .td-door__pill {
    position: absolute; top: clamp(20px,2.2vw,28px); right: clamp(20px,2.2vw,28px);
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 999px;
    border: 1px solid rgba(200,164,106,0.55); color: var(--td-gold-soft);
    font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.16em;
  }
  .td-door__title {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(26px,2.8vw,38px); color: #fff; line-height: 1.05;
    margin-top: auto;
  }
  .td-door__tag { color: var(--td-muted); font-size: clamp(14px,1.1vw,16px); margin-top: 10px; }

  /* Family / body screens ------------------------------------------------- */
  .td-h2 {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(30px,4.4vw,60px); line-height: 1.08; letter-spacing: -0.018em;
    color: #fff; margin: 0 auto 22px; max-width: 16ch;
  }
  .td-family-screen { max-width: 760px; }
  .td-body {
    color: rgba(238,243,248,0.72); font-size: clamp(15px,1.35vw,18px); line-height: 1.72;
    max-width: 60ch; margin: 0 auto clamp(26px,4vh,40px);
  }

  /* Business screen ------------------------------------------------------- */
  .td-biz-screen { max-width: 940px; }
  .td-chips {
    display: flex; flex-wrap: wrap; justify-content: center; gap: 10px;
    max-width: 820px; margin: 0 auto clamp(26px,4vh,40px);
  }
  .td-chip {
    padding: 10px 18px; border-radius: 999px; cursor: pointer;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.12);
    color: rgba(238,243,248,0.66); font-size: 14px; letter-spacing: 0.01em;
    transition: color 200ms ease, border-color 200ms ease, background 200ms ease;
  }
  .td-chip:hover { color: var(--td-text); border-color: rgba(200,164,106,0.4); }
  .td-chip:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-chip.is-active {
    color: var(--td-gold-soft); border-color: rgba(200,164,106,0.8);
    background: rgba(200,164,106,0.10);
  }
  .td-indcard {
    max-width: 720px; margin: 0 auto clamp(28px,4vh,40px);
    animation: td-cardfade 1100ms cubic-bezier(.22,.61,.36,1) both;
  }
  @keyframes td-cardfade {
    0%   { opacity: 0; transform: translateY(18px) scale(0.992); filter: blur(3px); }
    100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
  }
  .td-indcard__eyebrow {
    font-family: 'IBM Plex Mono', monospace; font-size: 12px; letter-spacing: 0.24em;
    text-transform: uppercase; color: var(--td-gold); margin: 0 0 16px;
  }
  .td-indcard__headline {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(28px,3.6vw,50px); line-height: 1.08; letter-spacing: -0.018em;
    color: #fff; margin: 0 auto 18px; max-width: 18ch;
  }
  .td-indcard__body {
    color: rgba(238,243,248,0.72); font-size: clamp(15px,1.3vw,17.5px); line-height: 1.7;
    max-width: 60ch; margin: 0 auto;
  }

  /* Question screens ------------------------------------------------------ */
  .td-q-screen { max-width: 620px; }
  .td-q-prompt {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(26px,3.2vw,42px); line-height: 1.14; letter-spacing: -0.014em;
    color: #fff; margin: 0 auto clamp(26px,4vh,36px); max-width: 20ch;
  }
  .td-options { display: flex; flex-direction: column; gap: 12px; text-align: left; }
  .td-option {
    display: flex; flex-direction: column; gap: 6px; width: 100%; cursor: pointer;
    padding: 18px 22px; border-radius: 13px;
    background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.12);
    transition: border-color 220ms ease, background 220ms ease, transform 220ms ease;
  }
  .td-option:hover { background: rgba(200,164,106,0.06); border-color: rgba(200,164,106,0.5); }
  .td-option:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-option.is-selected { border-color: rgba(200,164,106,0.85); background: rgba(200,164,106,0.1); }
  .td-option__label { font-family: 'Fraunces', serif; font-size: 18px; color: #fff; letter-spacing: -0.005em; }
  .td-option__body { font-size: 14px; line-height: 1.55; color: rgba(238,243,248,0.6); }

  .td-scale { display: grid; grid-template-columns: repeat(10, 1fr); gap: 7px; margin-top: 4px; }
  .td-dot {
    aspect-ratio: 1 / 1; min-height: 38px; border-radius: 10px; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'IBM Plex Mono', monospace; font-size: 14px; color: rgba(238,243,248,0.62);
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.12);
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
  }
  .td-dot:hover { border-color: rgba(200,164,106,0.5); color: #fff; }
  .td-dot:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-dot.is-active {
    background: var(--td-gold); border-color: var(--td-gold-soft); color: #14100a;
    font-weight: 600; transform: translateY(-1px);
  }
  .td-scale__labels {
    display: flex; justify-content: space-between; margin: 12px 0 4px;
    font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.05em;
    color: rgba(147,161,177,0.7);
  }

  /* Result screen — everything centered; reading kept as a side feature. ---- */
  .td-result-screen { max-width: 900px; }
  .td-result__headline {
    font-family: 'Fraunces', Georgia, serif; font-weight: 400;
    font-size: clamp(28px,3.6vw,46px); line-height: 1.1; letter-spacing: -0.018em;
    color: #fff; margin: 0 auto 18px; max-width: 20ch;
  }
  .td-result__body { color: rgba(238,243,248,0.76); font-size: clamp(15px,1.3vw,17px); line-height: 1.72; margin: 0 auto 26px; max-width: 56ch; }

  /* Balanced 3-track grid: an empty left spacer mirrors the reading rail, so the
     centre column (recommendation + actions) sits dead-centre while the reading
     stays off to the side. Collapses to one centred column below 900px. */
  .td-result-grid { display: grid; grid-template-columns: 1fr; justify-items: center; gap: 24px; }
  @media (min-width: 900px) {
    .td-result-grid { grid-template-columns: 1fr minmax(0,420px) 1fr; gap: 0 32px; align-items: start; justify-items: stretch; }
  }
  .td-result-spacer { display: none; }
  @media (min-width: 900px) { .td-result-spacer { display: block; } }

  .td-result-main { display: flex; flex-direction: column; align-items: center; gap: 24px; width: 100%; }
  .td-levers { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 11px; text-align: left; }
  .td-lever { display: flex; align-items: center; gap: 12px; font-size: 14.5px; color: rgba(238,243,248,0.6); }
  .td-lever__tick { width: 6px; height: 6px; border-radius: 999px; background: rgba(255,255,255,0.28); flex-shrink: 0; }
  .td-lever.is-emphasis { color: #fff; font-weight: 500; }
  .td-lever.is-emphasis .td-lever__tick { background: var(--td-gold); box-shadow: 0 0 0 4px rgba(200,164,106,0.2); }

  /* Note to Steck — collapsed by default (a quiet centred link) so it doesn't
     pull focus; expands to a small form only if the visitor wants to add one. */
  .td-note {
    max-width: 520px; margin: clamp(18px,3vh,30px) auto 0;
    display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center;
  }
  .td-note__toggle {
    display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
    background: none; border: none; padding: 8px 10px;
    font-size: 13px; letter-spacing: 0.01em; color: rgba(147,161,177,0.82);
    transition: color 200ms ease;
  }
  .td-note__toggle > svg { color: var(--td-gold); }
  .td-note__toggle:hover { color: var(--td-gold-soft); }
  .td-note__toggle:hover > svg { color: var(--td-gold-soft); }
  .td-note__toggle:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-note__label { font-size: 14px; color: rgba(238,243,248,0.78); }
  .td-note__input {
    width: 100%; box-sizing: border-box; resize: vertical; min-height: 70px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.14);
    border-radius: 10px; padding: 11px 13px; text-align: left;
    color: var(--td-text); font-family: inherit; font-size: 14px; line-height: 1.6;
    transition: border-color 200ms ease;
  }
  .td-note__input:focus { outline: none; border-color: rgba(200,164,106,0.6); }
  .td-note__input::placeholder { color: rgba(147,161,177,0.55); }
  .td-note__row { display: flex; gap: 10px; width: 100%; }
  @media (max-width: 520px) { .td-note__row { flex-direction: column; } }
  .td-note__field {
    flex: 1; min-width: 0; box-sizing: border-box;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.14);
    border-radius: 9px; padding: 10px 12px; text-align: left;
    color: var(--td-text); font-family: inherit; font-size: 14px;
    transition: border-color 200ms ease;
  }
  .td-note__field:focus { outline: none; border-color: rgba(200,164,106,0.6); }
  .td-note__field::placeholder { color: rgba(147,161,177,0.55); }
  .td-note__error { font-size: 12.5px; line-height: 1.5; color: #e8a0a0; margin: 0; }
  .td-note__send { align-self: center; padding: 10px 18px; font-size: 13.5px; }
  .td-note__send:disabled { opacity: 0.45; cursor: not-allowed; }
  .td-note__done { font-size: 13.5px; color: var(--td-gold-soft); margin: 0; }

  /* Recommended reading — the side feature (right rail; centred when stacked). */
  .td-result-aside { max-width: 264px; }
  @media (min-width: 900px) {
    .td-result-aside { justify-self: start; border-left: 1px solid rgba(255,255,255,0.08); padding-left: 28px; }
  }
  .td-related {
    display: block; text-decoration: none; text-align: left;
    margin: 0; padding: 15px 17px;
    border: 1px solid rgba(200,164,106,0.28); border-radius: 12px; background: rgba(200,164,106,0.04);
    transition: border-color 200ms ease, background 200ms ease;
  }
  .td-related:hover { border-color: rgba(200,164,106,0.6); background: rgba(200,164,106,0.08); }
  .td-related:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-related__eyebrow {
    display: block; font-family: 'IBM Plex Mono', monospace; font-size: 10.5px;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--td-gold); margin-bottom: 8px;
  }
  .td-related__title { display: flex; align-items: center; gap: 8px; font-size: 14.5px; font-weight: 500; color: #fff; line-height: 1.35; }
  .td-related__title > svg { color: var(--td-gold); flex-shrink: 0; }

  .td-positioning {
    font-size: 12px; line-height: 1.6; color: rgba(147,161,177,0.62); max-width: 52ch;
    margin: 0 auto 22px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.08);
  }
  /* Compliance line at the very bottom — quiet, centered, no heavy divider. */
  .td-positioning--foot {
    margin: 30px auto 0; padding-top: 18px; text-align: center;
    border-top: 1px solid rgba(255,255,255,0.06); color: rgba(147,161,177,0.5);
  }

  /* Buttons / CTAs -------------------------------------------------------- */
  .td-cta-row { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin: 0 0 20px; }
  .td-cta-row--result { flex-direction: column; align-items: stretch; justify-content: flex-start; max-width: 400px; margin: 0; }
  .td-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer;
    padding: 14px 26px; border-radius: 10px; font-size: 15px; font-weight: 600; letter-spacing: 0.01em;
    transition: transform 200ms ease, background 200ms ease, border-color 200ms ease, box-shadow 200ms ease, opacity 200ms ease;
  }
  .td-btn--lg { padding: 15px 28px; }
  .td-btn--primary {
    background: linear-gradient(180deg, var(--td-gold-soft) 0%, var(--td-gold) 100%);
    color: #14100a; border: 1px solid rgba(226,200,148,0.6);
    box-shadow: 0 16px 40px -16px rgba(200,164,106,0.6);
  }
  .td-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 20px 46px -16px rgba(200,164,106,0.75); }
  .td-btn--primary:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
  .td-btn--ghost {
    background: rgba(255,255,255,0.02); color: var(--td-text); border: 1px solid rgba(200,164,106,0.4);
  }
  .td-btn--ghost:hover { border-color: rgba(200,164,106,0.75); background: rgba(200,164,106,0.06); }
  .td-btn:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 3px; }
  .td-crosslink {
    display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
    background: none; border: none; padding: 6px;
    font-size: 13.5px; color: var(--td-muted); letter-spacing: 0.01em;
    transition: color 200ms ease;
  }
  .td-crosslink:hover { color: var(--td-gold-soft); }
  .td-crosslink:hover > svg { color: var(--td-gold-soft); }
  .td-crosslink > svg { color: var(--td-gold); }
  .td-crosslink:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }
  .td-secondary {
    display: block; margin: 4px auto 0; background: none; border: none; cursor: pointer; padding: 8px;
    font-size: 13px; color: var(--td-muted); transition: color 200ms ease;
  }
  .td-secondary:hover { color: var(--td-text); }
  .td-secondary:focus-visible { outline: 2px solid var(--td-gold); outline-offset: 2px; }

  /* ── Mobile: every screen fits the phone without page scrolling ───────── */
  @media (max-width: 640px) {
    .td-stage { padding: clamp(86px,13vh,110px) 20px clamp(28px,5vh,44px); align-items: flex-start; }
    .td-chrome-tl { gap: 12px; }
    .td-h1 { font-size: clamp(34px,10vw,46px); margin-bottom: 14px; }
    .td-sub { margin-bottom: 22px; font-size: 14px; }
    .td-doors { grid-template-columns: 1fr; gap: 12px; margin-bottom: 20px; }
    .td-door { min-height: 104px; padding: 16px 18px; animation: none; }
    .td-door__num { font-size: 13px; }
    .td-door__pill { top: 14px; right: 14px; padding: 5px 11px; font-size: 10px; }
    .td-door__title { font-size: 22px; margin-top: 14px; }
    .td-door__tag { font-size: 13px; margin-top: 6px; }
    .td-h2 { font-size: clamp(24px,7vw,32px); }
    .td-body { font-size: 14.5px; margin-bottom: 22px; }
    .td-chips { gap: 8px; margin-bottom: 20px; }
    .td-chip { padding: 8px 13px; font-size: 12.5px; }
    .td-indcard__headline { font-size: clamp(23px,6.4vw,30px); }
    .td-indcard__body { font-size: 14.5px; }
    .td-indcard { margin-bottom: 22px; }
    .td-q-prompt { font-size: clamp(22px,6.4vw,28px); }
    .td-option { padding: 14px 16px; }
    .td-option__label { font-size: 16px; }
    .td-option__body { font-size: 13px; }
    .td-dot { min-height: 30px; border-radius: 8px; font-size: 12px; }
    .td-scale { gap: 5px; }
    .td-result__headline { font-size: clamp(24px,6.6vw,32px); }
    .td-btn { width: 100%; padding: 13px 20px; font-size: 14px; }
    .td-cta-row { flex-direction: column; }
    .td-frame { inset: 10px; }
  }

  /* Very short viewports (landscape phones) — trim vertical rhythm. */
  @media (max-height: 560px) {
    .td-stage { padding-top: 76px; padding-bottom: 24px; }
    .td-door { min-height: 92px; }
    .td-h1 { font-size: clamp(30px,7vw,40px); }
  }

  /* Reduced motion: disable decorative breathing + entrance rises, but NOT
     the functional industry rotation (that's JS-driven and unaffected). */
  @media (prefers-reduced-motion: reduce) {
    .td-ground, .td-frame { transition: none; }
    .td-screen, .td-indcard, .td-door { animation: none; }
    .td-door { transform: none; }
  }
`;

export default TwoDoorsExperience;
